import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Download, Sparkles } from "lucide-react";
import { BRAND } from "@/constants/brand";

export default function RequestProductPoster() {
  const url = useMemo(() => {
    // Prefer the production brand domain; fall back to current origin for preview.
    const host = typeof window !== "undefined" ? window.location.origin : "";
    const prod = "https://thepoppanda.com";
    const base = host.includes("thepoppanda.com") ? prod : (host || prod);
    return `${base}/request-product`;
  }, []);

  const printPoster = () => window.print();

  const downloadSvg = () => {
    const svg = document.querySelector("#poster-qr svg");
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pop-panda-request-qr.svg"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] poster-page" data-testid="qr-poster-page">
      {/* No-print toolbar */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-brand-line px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Shop QR Poster</div>
          <div className="font-display text-xl">Pop Panda · Request a Product</div>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadSvg} className="btn-pill btn-ghost text-xs" data-testid="btn-download-qr"><Download size={11} /> Download QR (SVG)</button>
          <button onClick={printPoster} className="btn-pill btn-dark text-xs" data-testid="btn-print-poster"><Printer size={11} /> Print A4 Poster</button>
        </div>
      </div>

      {/* A4 poster — portrait 210x297mm. Scales down on screen, prints full-size. */}
      <div className="mx-auto my-6 bg-white shadow-lg print:shadow-none print:my-0 poster-sheet" data-testid="poster-sheet">
        {/* Yellow top band */}
        <div className="relative overflow-hidden" style={{ background: "#FEED02", padding: "28mm 22mm 14mm" }}>
          <div className="flex items-center gap-4 mb-6">
            <img src={BRAND.logoUrl} alt="Pop Panda" className="h-16 w-16 object-contain" crossOrigin="anonymous" />
            <div>
              <div className="font-display font-black text-5xl leading-none text-[#111]">Pop Panda</div>
              <div className="text-sm tracking-[0.3em] uppercase text-[#111]/70 mt-1">{BRAND.tagline}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#111]/60 mb-3">Can't find your favourite?</div>
          <h1 className="font-display font-black text-[64px] leading-[0.95] text-[#111] mb-4" style={{ letterSpacing: "-0.01em" }}>
            Tell us<br />what to<br />stock next.
          </h1>
        </div>

        {/* Body */}
        <div className="relative" style={{ padding: "16mm 22mm 22mm" }}>
          <div className="grid grid-cols-[1fr_auto] gap-8 items-center">
            {/* Left: instructions */}
            <div>
              <div className="space-y-5">
                <Step n="1" title="Scan this QR" body="With your phone camera — no app, no sign-up needed." />
                <Step n="2" title="Tell us the product" body="Name, category, a photo if you have one. 30 seconds, that's it." />
                <Step n="3" title="We'll bring it in" body="If enough people want it, we'll stock it. You'll get a tracking link too." />
              </div>
              <div className="mt-8 rounded-2xl bg-[#111] text-white p-5 flex items-start gap-3">
                <Sparkles size={18} className="text-[#FEED02] mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-xl leading-tight">Already requested?</div>
                  <div className="text-sm text-white/70 mt-0.5">Scan, tap "Explore Requested Products" and add your demand to help it come in sooner.</div>
                </div>
              </div>
            </div>

            {/* Right: QR tile */}
            <div className="text-center">
              <div id="poster-qr" className="rounded-3xl p-4 bg-white border-[6px] border-[#111] inline-block">
                <QRCodeSVG
                  value={url}
                  size={260}
                  level="H"
                  includeMargin={false}
                  fgColor="#111111"
                  bgColor="#FFFFFF"
                  imageSettings={{
                    src: BRAND.logoUrl,
                    height: 44, width: 44,
                    excavate: true,
                  }}
                />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#111]/60">Scan to request</div>
              <div className="mt-1 font-mono text-xs text-[#111]/80 break-all max-w-[260px] mx-auto">{url.replace(/^https?:\/\//, "")}</div>
            </div>
          </div>
        </div>

        {/* Footer band */}
        <div style={{ background: "#111", padding: "10mm 22mm", color: "#FEED02" }} className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-xl">The Pop Panda</div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#FEED02]/80">Community Wishlist · thepoppanda.com</div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        .poster-sheet {
          width: 210mm; min-height: 297mm;
          display: flex; flex-direction: column;
        }
        .poster-sheet > div:nth-child(2) { flex: 1; }
        @media screen and (max-width: 900px) {
          .poster-sheet { transform: scale(0.6); transform-origin: top center; }
        }
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { background: #fff !important; }
          .poster-page { background: #fff !important; }
          .poster-sheet { width: 210mm; height: 297mm; box-shadow: none !important; margin: 0 !important; transform: none !important; }
        }
      `}</style>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-11 h-11 rounded-full bg-[#FEED02] grid place-items-center font-display font-black text-xl text-[#111] border-2 border-[#111]">{n}</div>
      <div>
        <div className="font-display text-2xl leading-tight text-[#111]">{title}</div>
        <div className="text-sm text-[#111]/70 mt-0.5">{body}</div>
      </div>
    </div>
  );
}
